import { omit } from "lodash";
import { fabric } from "fabric";
import { Spinner } from "./spinner";
import roundNumber from "../../rounded-number";
import {
  Canvas,
  IGroupOptions,
  IImageOptions,
  ITextboxOptions,
  Object,
} from "fabric/fabric-impl";

export class BorderedTextBox extends fabric.Group {
  id?: string;

  constructor(text: string, options?: ITextboxOptions, id?: string) {
    const textBox = new fabric.Textbox(
      text,
      omit(options ?? {}, ["angle", "left", "top", "height"]),
    );
    super([textBox], {
      left: options?.left ?? 0,
      top: options?.top ?? 0,
      angle: options?.angle ?? 0,
      height: textBox.height,
      lockScalingY: options?.lockScalingY ?? false,
      lockScalingFlip: true,
    });
    this.add(this.createBorderRect());
    this.id = id ?? new Date().getTime().toString();
  }

  setOptions(options?: ITextboxOptions & { id?: string }): void {
    const textBox = this.getTextbox();
    const textBoxOptions = omit(options ?? {}, [
      "angle",
      "left",
      "top",
      "height",
    ]);

    super.setOptions({
      left: options?.left ?? this.left,
      top: options?.top ?? this.top,
      angle: options?.angle ?? this.angle,
      width: textBox.width,
      height: textBox.height,
      lockScalingY: options?.lockScalingY ?? false,
      lockScalingFlip: true,
    });

    textBox.setOptions({
      ...textBoxOptions,
      left: -(this.width ?? 0) / 2,
      top: -(this.height ?? 0) / 2,
    });

    const borderElm = this.getBorderRect();
    if (borderElm) {
      borderElm.setOptions(this.getBorderOptions());
    }

    if (options?.id) {
      this.set("id", options.id);
    }
    this.setCoords();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    const textBox = this.getTextbox();
    if (textBox.height !== this.height) {
      this.setOptions({ height: textBox.height });
    }
  }

  setText(text: string): void {
    const textBox = this.getTextbox();
    textBox.set("text", text);

    this.height = textBox.height;
  }

  getTextbox(): fabric.Textbox {
    const objects = this.getObjects("textbox") as fabric.Textbox[];
    return objects[0];
  }

  getBorderRect(): fabric.Rect {
    const objects = this.getObjects("rect") as fabric.Rect[];
    return objects[0];
  }

  createBorderRect(): fabric.Rect {
    return new fabric.Rect(this.getBorderOptions());
  }

  getBorderOptions(): fabric.IRectOptions {
    const textBox = this.getTextbox();
    const { strokeStyle, lineWidth } = textBox.data ?? {};

    const shouldRenderWithRoundCorner = !lineWidth && textBox.selectable;

    const anchorPoint = textBox.getCenterPoint();
    const defaultLineWith = !textBox.selectable ? 0 : 1;
    const borderOptions = getOutlinedBoxProperties({
      anchorPoint,
      angle: 0,
      width: textBox.width,
      height: textBox.height,
      lineWidth: shouldRenderWithRoundCorner ? defaultLineWith : lineWidth,
      strokeStyle: shouldRenderWithRoundCorner ? "#e9e9e9" : strokeStyle,
    });

    borderOptions.rx = shouldRenderWithRoundCorner ? 5 : 0;
    borderOptions.ry = shouldRenderWithRoundCorner ? 5 : 0;
    return borderOptions;
  }
}

export const ImageLoaderDelay = 700;

export class BorderedImage extends fabric.Image {
  private readonly _element: HTMLImageElement | undefined;
  private readonly _spinner: Spinner;
  private loading: boolean = false;
  private loadingTimeout: number | undefined;

  constructor(element: HTMLImageElement, options?: IImageOptions) {
    super(element, options);
    this._element = element;
    this._spinner = new Spinner();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);

    const { top = 0, left = 0, width = 1, height = 1 } = this;

    this.spinner.set({ left, top, width, height });

    if (this.loading) {
      this.spinner.render(ctx);
    }

    renderBoxAroundElement(this, ctx);
  }

  get spinner(): Spinner {
    return this._spinner;
  }

  loadImage(url: string): void {
    if (!url) {
      return;
    }

    if (!this.loading) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = window.setTimeout(
        () => this.showLoader(),
        ImageLoaderDelay,
      );
    }

    fabric.Image.fromURL(
      url,
      (img) => {
        const { width = 1, height = 1 } = this;

        if (
          img.getElement().className === "canvas-img" &&
          img.width &&
          img.height
        ) {
          const scale = Math.min(width / img.width, height / img.height);
          this.setElement(img.getElement(), {
            scaleX: roundNumber(scale, 2),
            scaleY: roundNumber(scale, 2),
          });
        } else {
          this.setElement(new Image(150, 150), { width, height });
        }

        this.canvas?.requestRenderAll();
        this.setCoords();
        this.hideLoader();
      },
      { crossOrigin: "anonymous" },
    );
  }

  showLoader(): void {
    if (!this.canvas || this.loading) {
      return;
    }

    this.loading = true;
    this.canvas.add(this.spinner);
    this.spinner.startAnimation();
    this.canvas?.requestRenderAll();
  }

  hideLoader(): void {
    clearTimeout(this.loadingTimeout);
    this.loading = false;
    this.canvas?.remove(this.spinner);
    this.spinner.stopAnimation();
    this.canvas?.requestRenderAll();
  }

  isLoading(): boolean {
    return this.loading;
  }

  /**
   * We need to convert the image to Base64 when converting it to SVG so it doesn't reference an external URL.
   */
  getSvgSrc(): string {
    return this.toDataURLforSVG();
  }

  toDataURLforSVG(): string {
    const el = fabric.util.createCanvasElement();
    if (!this._element) {
      return "";
    }

    el.width = this._element.naturalWidth || this._element.width;
    el.height = this._element.naturalHeight || this._element.height;
    el.getContext("2d")?.drawImage(this._element, 0, 0);
    return el.toDataURL();
  }
}

export class BorderedText extends fabric.Text {
  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    renderBoxAroundElement(this, ctx);
  }
}

export class BorderedGroup extends fabric.Group {
  id: string;

  constructor(
    objects?: Object[],
    options?: IGroupOptions,
    isAlreadyGrouped?: boolean,
    id?: string,
  ) {
    super(objects, options, isAlreadyGrouped);
    this.id = id ?? new Date().getTime().toString();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    renderBoxAroundElement(this, ctx, this.id);
  }
}

export const renderBoxAroundElement = (
  obj: fabric.Object,
  ctx: CanvasRenderingContext2D,
  objectId: string = "",
): void => {
  // Renders a border around the element. If the element does not specify border styles,
  // render a rounded, light-gray border instead
  const { strokeStyle, lineWidth } = obj.data ?? {};
  const {
    left: groupLeftPos = 0,
    top: groupTopPos = 0,
    height: groupHeight = 0,
    width: groupWidth = 0,
  } = obj.group ?? {};
  const shouldRenderWithRoundCorner = !lineWidth && obj.selectable;
  const shouldRenderWithWidth = !!lineWidth;

  if (shouldRenderWithRoundCorner) {
    removeOutlinedBox(obj.canvas, objectId);

    const anchorPoint = obj.getPointByOrigin("left", "top");
    anchorPoint.x = anchorPoint.x + groupLeftPos + groupWidth / 2;
    anchorPoint.y = anchorPoint.y + groupTopPos + groupHeight / 2;

    renderShadedBox(
      ctx,
      anchorPoint,
      obj.angle,
      obj.getScaledWidth(),
      obj.getScaledHeight(),
    );

    obj.setCoords();
  }

  if (shouldRenderWithWidth) {
    const anchorPoint = obj.getCenterPoint();
    anchorPoint.x = anchorPoint.x + groupLeftPos + groupWidth / 2;
    anchorPoint.y = anchorPoint.y + groupTopPos + groupHeight / 2;

    renderOutlinedBox({
      canvas: obj.canvas,
      objectId,
      anchorPoint,
      angle: obj.angle,
      width: obj.width,
      height: obj.height,
      lineWidth,
      strokeStyle,
    });
  }
};

export const renderShadedBox = (
  ctx: CanvasRenderingContext2D,
  anchorPoint: fabric.Point,
  angle: number | undefined,
  width: number,
  height: number,
): void => {
  // Save context before its transformations
  ctx.save();

  const normalizedAngle = Math.floor((angle ?? 0) % 360);
  ctx.translate(anchorPoint.x, anchorPoint.y);
  ctx.rotate(normalizedAngle * (Math.PI / 180));
  ctx.translate(-anchorPoint.x, -anchorPoint.y);

  // Start drawing the element's border
  ctx.beginPath();

  const padding = 1;
  const x = anchorPoint.x - padding;
  const y = anchorPoint.y - padding;
  const widthWithPadding = width + 2 * padding;
  const heightWithPadding = height + 2 * padding;
  ctx.lineWidth = 1;
  ctx.strokeStyle = "#e9e9e9";

  if (ctx.roundRect) {
    ctx.roundRect(x, y, widthWithPadding, heightWithPadding, [4]);
  } else {
    ctx.rect(x, y, widthWithPadding, heightWithPadding);
  }

  // Finishes drawing the border
  ctx.closePath();
  ctx.stroke();

  // Restore context to its original state to reset transformations made
  ctx.resetTransform();
  ctx.restore();
};

export const getOutlinedBox = (
  canvas: Canvas | undefined,
  objectId: string,
): fabric.Rect | undefined => {
  if (!canvas) {
    return;
  }

  const rectId = getOutlinedBoxId(objectId);

  return canvas.getObjects().find((obj) => {
    if ("id" in obj) {
      return obj.id === rectId;
    }
    return false;
  });
};

interface OutlinedBoxProperties {
  anchorPoint: fabric.Point;
  angle: number | undefined;
  width: number | undefined;
  height: number | undefined;
  lineWidth: number | undefined;
  strokeStyle: string | undefined;
}

const getOutlinedBoxProperties = ({
  anchorPoint,
  angle,
  width = 1,
  height = 1,
  lineWidth = 0,
  strokeStyle = "#000",
}: OutlinedBoxProperties): fabric.IRectOptions => {
  const padding = 1;

  const normalizedAngle = Math.floor((angle ?? 0) % 360);

  return {
    angle: normalizedAngle,
    left: anchorPoint.x,
    top: anchorPoint.y,
    width: width + lineWidth + padding,
    height: height + lineWidth + padding,
    fill: "transparent",
    stroke: strokeStyle,
    strokeWidth: lineWidth,
    selectable: false,
    originX: "center",
    originY: "center",
    evented: false,
  };
};

interface OutlinedBox {
  canvas: Canvas | undefined;
  objectId: string;
  anchorPoint: fabric.Point;
  angle: number | undefined;
  width: number | undefined;
  height: number | undefined;
  lineWidth: number | undefined;
  strokeStyle: string | undefined;
}

export const renderOutlinedBox = ({
  canvas,
  objectId,
  anchorPoint,
  angle,
  width,
  height,
  lineWidth,
  strokeStyle,
}: OutlinedBox): fabric.Rect | undefined => {
  if (!canvas) {
    return;
  }

  const rectProperties = getOutlinedBoxProperties({
    anchorPoint,
    angle,
    width,
    height,
    lineWidth,
    strokeStyle,
  });

  const currentRect = getOutlinedBox(canvas, objectId);
  if (currentRect) {
    currentRect.set(rectProperties);
    return;
  }

  const rectId = getOutlinedBoxId(objectId);
  const newCurrentRect = new fabric.Rect(rectProperties);
  newCurrentRect.setOptions({ id: rectId });
  canvas.add(newCurrentRect);
};

export const removeOutlinedBox = (
  canvas: Canvas | undefined,
  objectId: string,
): void => {
  if (!canvas || !objectId) {
    return;
  }

  const currentRect = getOutlinedBox(canvas, objectId);
  if (currentRect) {
    canvas.remove(currentRect);
  }
};

export const getOutlinedBoxId = (objectId: string): string => {
  return `${objectId}-rect`;
};
